import { FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createAdminAccount } from './adminService';
import { ADMIN_ROLE_LABEL, AdminRole, useAdminSession } from './adminSession';
import styles from './Admin.module.scss';

function AdminSignup() {
  const navigate = useNavigate();
  const [session] = useAdminSession();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<AdminRole>('ADMIN');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isSuperAdmin = session?.admin.role === 'SUPER_ADMIN';

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await createAdminAccount(session?.token ?? null, {
        username,
        password,
        name: name || undefined,
        email: email || undefined,
        role: isSuperAdmin ? role : undefined,
      });
      setUsername('');
      setPassword('');
      setPasswordConfirm('');
      setName('');
      setEmail('');
      setRole('ADMIN');

      if (result.status === 'PENDING') {
        setSuccess('가입 신청이 완료되었습니다. 최종관리자가 승인하면 로그인할 수 있습니다.');
      } else {
        setSuccess('관리자 계정이 생성되었습니다.');
        if (!session) {
          navigate('/admin/login');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '관리자 계정 생성 중 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className={styles.authPage}>
      <div className={styles.card}>
        <div className={styles.brandMark}>G</div>
        <h1>관리자 회원가입</h1>
        {!session && (
          <p>
            등록된 관리자가 없는 경우 인증 없이 첫 계정(최종관리자)을 만들 수 있습니다. 이미 관리자가 있다면
            가입 신청 후 최종관리자의 승인을 받아야 로그인할 수 있습니다.
          </p>
        )}
        <form className={styles.uploadForm} onSubmit={handleSubmit}>
          <label htmlFor="admin-username">아이디</label>
          <input
            id="admin-username"
            type="text"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            required
          />

          <label htmlFor="admin-password">비밀번호</label>
          <input
            id="admin-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
          />

          <label htmlFor="admin-password-confirm">비밀번호 확인</label>
          <input
            id="admin-password-confirm"
            type="password"
            value={passwordConfirm}
            onChange={(event) => setPasswordConfirm(event.target.value)}
            required
          />

          <label htmlFor="admin-name">이름 (선택)</label>
          <input id="admin-name" type="text" value={name} onChange={(event) => setName(event.target.value)} />

          <label htmlFor="admin-email">이메일 (선택)</label>
          <input id="admin-email" type="email" value={email} onChange={(event) => setEmail(event.target.value)} />

          {isSuperAdmin && (
            <>
              <label htmlFor="admin-role">권한</label>
              <select id="admin-role" value={role} onChange={(event) => setRole(event.target.value as AdminRole)}>
                {(Object.keys(ADMIN_ROLE_LABEL) as AdminRole[]).map((value) => (
                  <option key={value} value={value}>
                    {ADMIN_ROLE_LABEL[value]}
                  </option>
                ))}
              </select>
            </>
          )}

          <button type="submit" disabled={submitting}>
            {submitting ? '생성 중...' : '계정 생성'}
          </button>
        </form>
        {error && <p className={styles.error}>{error}</p>}
        {success && <p className={styles.success}>{success}</p>}
        <p>
          <Link to={session ? '/admin' : '/admin/login'}>← {session ? '관리자 대시보드로' : '로그인으로'}</Link>
        </p>
      </div>
    </section>
  );
}

export default AdminSignup;
